import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { retry } from 'rxjs';
import { LogLevel } from 'src/common/dtos/client-log.dto';

import { environment } from 'src/environments/environment';
import { SpoidDescrambler } from '@vanguard/vg-spoid-descrambler';

@Injectable({
    providedIn: 'root',
})
export class LoggerService {
    private readonly httpClient = inject(HttpClient);


    /**
     * Logs an informational message to the server.
     * 
     * @param message - The message to log
     */
    info(message: string) {
        this.logServerSide(LogLevel.INFO, message);
    }

    /**
     * Logs a warning message to the server.
     * 
     * @param message - The warning message to log
     */
    warn(message: string) {
        this.logServerSide(LogLevel.WARN, message);
    }

    /**
     * Logs an error message to the server with optional stack trace.
     * 
     * @param message - The error message to log
     * @param stackTrace - Optional stack trace information
     */
    error(message: string, stackTrace?: string) {
        this.logServerSide(LogLevel.ERROR, message, stackTrace);
    }

    /**
     * Sends a log message to the server-side logging API.
     * 
     * @param level - The log level (INFO, WARN, ERROR)
     * @param message - The message to log
     * @param stackTrace - Optional stack trace for errors
     * 
     * @private
     * @remarks
     * - Automatically retries failed requests up to 2 times
     * - Formats messages with the user's POID for tracking
     * - Errors during logging are only logged to console (prevents infinite loops)
     */
    private logServerSide(level: LogLevel, message: string, stackTrace?: string) {
        const log = {
            level: level,
            message: this.formatMessage(stackTrace ? `${message}: ${stackTrace}` : message),
        };
        this.httpClient
            .post(`${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.CLIENT_LOG}`, log)
            .pipe(retry(2))
            .subscribe({
                next: () => { },
                error: console.log,
            });
    }

    /**
     * Formats log messages with additional context.
     * 
     * @param message - The original message
     * @returns Formatted message with "Serverless:" prefix and user POID
     * 
     * @remarks
     * Retrieves SPOID from window object (set by ZciCookieService) and descrambles it
     * to get the actual POID. This helps track which user generated each log entry.
     * 
     * Note: Cannot import ZciCookieService due to circular dependency.
     */
    formatMessage(message: string): string {
        // ZciCookieService already exports spoid in window
        // importing ZciCookieService here causes circular dependency
        // @ts-expect-error spoid is not defined in window
        return `Serverless: ${message} POID: ${SpoidDescrambler.descrambleSpoid(window.spoid)}`
    }
}
